// src/components/DashboardView.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Sparkles, Lock,
    TrendingUp, Battery, ShieldAlert, Zap, X,
    Target, Activity, LineChart as LineChartIcon, Loader2,
    Watch, Smartphone, Plus, Link as LinkIcon, Trash2, RefreshCw, AlertCircle, Info,
    HeartHandshake, LifeBuoy
} from 'lucide-react';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { calculateScores } from '../utils/scoring';
import { SQUAD_PLAYBOOKS } from '../data/content'; 
import { useOrganization } from '../context/OrganizationContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'; 
import { db, functions } from '../config/firebase-config';
import { httpsCallable } from 'firebase/functions';
import PerformanceWellbeingMap from './dashboard/PerformanceWellbeingMap';
import GuidedPath from './dashboard/GuidedPath';
import SupportContactModal from './dashboard/SupportContactModal';
import EapEntryChip from './dashboard/EapEntryChip';
import { resolveSupportContact, hasReachableContact, resolveEapProgram } from '../utils/resolveSupportContact';
import { useOrgVoice } from '../hooks/useOrgVoice';
import { isPulseDueFor } from '../utils/pulse';
import { heroClass, heroStyle, isHubTheme } from '../utils/orgTheme';
import { resolveAgeTier } from '../utils/ageTier';
import { youthQuadrant, youthTier, YOUTH_ZONE_LABELS } from '../data/youthCopy';

// --- WEARABLE CONFIGURATION ---
const REDIRECT_URI = window.location.origin;

// Providers whose OAuth URLs still carry a placeholder client id (e.g. client_id=YOUR_OURA_ID)
// are not live yet — they render as disabled "Coming Soon" and never navigate. Swapping a real
// client id into the authUrl automatically re-enables the button.
const isPlaceholderAuthUrl = (url) => !!url && url.includes('YOUR_');

const PROVIDERS = [
    { 
        id: 'fitbit', 
        name: 'Fitbit', 
        icon: <Watch size={24}/>, 
        color: 'text-emerald-600', 
        bg: 'bg-emerald-50',
        authUrl: `https://www.fitbit.com/oauth2/authorize?response_type=code&client_id=23TSD9&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=activity%20heartrate%20sleep%20profile&expires_in=604800`
    },
    { 
        id: 'oura', 
        name: 'Oura Ring', 
        icon: <Activity size={24}/>, 
        color: 'text-slate-800', 
        bg: 'bg-slate-100',
        authUrl: `https://cloud.ouraring.com/oauth/authorize?response_type=code&client_id=YOUR_OURA_ID&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`
    },
    { 
        id: 'whoop', 
        name: 'Whoop', 
        icon: <Zap size={24}/>, 
        color: 'text-red-600', 
        bg: 'bg-red-50',
        authUrl: `https://api.prod.whoop.com/oauth/oauth2/auth?response_type=code&client_id=YOUR_WHOOP_ID&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=read:recovery`
    },
    { 
        id: 'apple', 
        name: 'Apple Health', 
        icon: <Smartphone size={24}/>, 
        color: 'text-blue-600', 
        bg: 'bg-blue-50',
        authUrl: null // Apple Health is mobile-only via HealthKit
    }
];

// Small reusable section label: uppercase brand eyebrow + muted subtext + hairline rule
const SectionEyebrow = ({ label, sub }) => (
    <div className="flex items-baseline gap-3 px-1 pt-2">
        <span className="text-[11px] font-black uppercase tracking-widest text-brand-primary whitespace-nowrap">{label}</span>
        {sub && <span className="text-xs text-slate-400 font-medium hidden sm:inline truncate">{sub}</span>}
        <span className="flex-1 h-px bg-slate-200 self-center" aria-hidden="true" />
    </div>
);

const DashboardView = ({ onNavigate, currentUser }) => {
    
    // --- CONTEXT ---
    const { orgData, isPreviewMode } = useOrganization();
    
    // --- STATE ---
    // configRules value is not read yet (rules are fetched for future gating) —
    // only the setter is destructured to keep lint clean.
    const [, setConfigRules] = useState(null);
    const [activePlaybook, setActivePlaybook] = useState(null);
    const [playbookAuthor, setPlaybookAuthor] = useState("");

    // HISTORICAL DATA STATE
    const [historicalData, setHistoricalData] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);
    const [selectedTrend, setSelectedTrend] = useState('perfScore');

    // --- WEARABLE STATE ---
    const [isConnecting, setIsConnecting] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [wearableData, setWearableData] = useState({}); 
    const [syncStatus, setSyncStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState(null);
    const [showConnectModal, setShowConnectModal] = useState(false);
    // UI: explanation pop-ups
    const [showStreakInfo, setShowStreakInfo] = useState(false);
    const [showForecastInfo, setShowForecastInfo] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(false);
    // 'crisis' opens the outlet on the urgent lines; null opens it normally.
    const [supportFocus, setSupportFocus] = useState(null);

    // Per-org "personal connection outlet" — configured humans members can reach.
    // Contacts are tagged with the member types they serve (empty = everyone), so
    // e.g. former players reach one person while spouses/kids reach another. Resolve
    // the one that fits THIS member, with a graceful legacy single-contact fallback.
    const supportContact = useMemo(
        () => resolveSupportContact(orgData, currentUser?.memberType),
        [orgData, currentUser?.memberType]
    );
    // The org's EAP/EFAP program (docs/plans/EAP-TIEIN-PLAN.md) — routes members into the
    // plan's actual service providers; also serves as the adult crisis fallback.
    const eapProgram = useMemo(
        () => resolveEapProgram(orgData, currentUser?.memberType),
        [orgData, currentUser?.memberType]
    );
    // Used for copy only — the modal always has the Canada-correct floor, so the
    // outlet itself is always available.
    const hasSupportContact = hasReachableContact(supportContact) || !!eapProgram;
    void hasSupportContact;

    // Playbook copy is authored in sport voice; adapt it for corporate orgs
    // exactly like SquadDashboard does (shared hook).
    const { adapt: adaptText } = useOrgVoice();
    const hasFetchedCode = useRef(false);

    // --- EFFECT: FETCH RULES FROM SOURCE OF TRUTH ---
    useEffect(() => {
        const fetchConfigRules = async () => {
            if (!orgData?.activeAssessmentId) {
                setConfigRules({ includeCognitive: true });
                return;
            }
            try {
                const configRef = doc(db, "assessment_configs", orgData.activeAssessmentId);
                const configSnap = await getDoc(configRef);
                if (configSnap.exists()) {
                    setConfigRules(configSnap.data().configOptions || { includeCognitive: true });
                } else {
                    setConfigRules({ includeCognitive: true });
                }
            } catch (error) {
                console.error("Error loading config rules:", error);
            }
        };
        fetchConfigRules();
    }, [orgData]);

    // --- EFFECT: FETCH SQUAD PLAYBOOK ---
    useEffect(() => {
        const fetchPlaybook = async () => {
            if (currentUser?.organizationId && currentUser?.groupId) {
                try {
                    const pbRef = doc(db, "organizations", currentUser.organizationId, "active_playbooks", currentUser.groupId);
                    const pbSnap = await getDoc(pbRef);
                    if (pbSnap.exists()) {
                        const data = pbSnap.data();
                        const pbContent = SQUAD_PLAYBOOKS[data.playbookId];
                        if (pbContent) {
                            setActivePlaybook(pbContent);
                            setPlaybookAuthor(data.deployedBy || "Coach");
                        }
                    }
                } catch (e) { console.error("Error fetching squad playbook:", e); }
            }
        };
        fetchPlaybook();
    }, [currentUser]);

    // --- EFFECT: FETCH HISTORICAL USER ASSESSMENTS ---
    useEffect(() => {
        const fetchHistory = async () => {
            if (!currentUser?.uid) return;
            setIsHistoryLoading(true);
            try {
                const q = query(collection(db, "assessments"), where("userId", "==", currentUser.uid));
                const snap = await getDocs(q);
                
                const rawDocs = snap.docs.map(d => d.data());
                rawDocs.sort((a, b) => new Date(a.completedAt) - new Date(b.completedAt));
                const recentDocs = rawDocs.slice(-14);

                const formattedHistory = recentDocs.map(data => {
                    const date = new Date(data.completedAt);
                    let perfTotal = 0;
                    let count = 0;
                    const perfKeys = ['focus', 'resilience', 'motivation', 'confidence', 'discipline', 'awareness', 'adaptability', 'sleep_perf', 'nutrition', 'injury_mgmt'];
                    perfKeys.forEach(k => {
                        if (data.scores?.[k] !== undefined) {
                            perfTotal += data.scores[k];
                            count++;
                        }
                    });
                    const calcPerf = count > 0 ? Math.round(perfTotal / count) : 0;

                    return {
                        dateStr: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        perfScore: data.scores?.perfScore || calcPerf,
                        ...data.scores 
                    };
                });

                setHistoricalData(formattedHistory);
            } catch (error) {
                console.error("Error fetching historical data:", error);
            } finally {
                setIsHistoryLoading(false);
            }
        };
        fetchHistory();
    }, [currentUser]);

    // =========================================================================
    // WEARABLE LOGIC BLOCK
    // =========================================================================
    const checkConnectionStatus = useCallback(async () => {
        if (!currentUser?.uid) return;
        try {
            // Wearable summaries live in the self-only private_metrics doc;
            // the user doc is only a legacy fallback until migration runs.
            let wearables = null;
            const privSnap = await getDoc(doc(db, "users", currentUser.uid, "private_metrics", "latest"));
            if (privSnap.exists() && privSnap.data().wearables) {
                wearables = privSnap.data().wearables;
            } else {
                const docSnap = await getDoc(doc(db, "users", currentUser.uid));
                if (docSnap.exists()) wearables = docSnap.data().wearables;
            }
            if (wearables && Object.keys(wearables).length > 0) {
                setWearableData(wearables);
            }
        } catch (e) { console.error("Status Check Error", e); }
    }, [currentUser?.uid]);

    const handleRefreshData = useCallback(async (providerId) => {
        setSyncStatus('syncing');
        setErrorMessage(null);
        try {
            const refreshFn = httpsCallable(functions, 'refreshWearableData');
            const result = await refreshFn({ provider: providerId });
            
            if (result.data.success) {
                setWearableData(prev => ({ ...prev, [providerId]: result.data.data }));
                setSyncStatus('success');
            } else {
                throw new Error("Sync returned failure");
            }
        } catch (error) {
            console.error("Sync Error:", error);
            setSyncStatus('error');
            setErrorMessage("Failed to sync. " + error.message);
        } finally {
            setTimeout(() => setSyncStatus('idle'), 2000);
        }
    }, []);

    const handleOAuthCallback = useCallback(async (code, providerId) => {
        setIsConnecting(true);
        setSyncStatus('syncing');
        setErrorMessage(null);
        
        window.history.replaceState({}, document.title, window.location.hash);
        localStorage.removeItem('pendingWearableProvider'); 

        try {
            const exchangeFn = httpsCallable(functions, 'exchangeWearableToken');
            await exchangeFn({ code, redirectUri: REDIRECT_URI, provider: providerId });
            await handleRefreshData(providerId);
        } catch (error) {
            console.error(`${providerId} Connection Failed:`, error);
            setSyncStatus('error');
            if (!error.message.includes("invalid")) {
                 setErrorMessage(error.message);
                 alert("Connection Failed: " + error.message);
            } else {
                checkConnectionStatus();
            }
        } finally {
            setIsConnecting(false);
        }
    }, [handleRefreshData, checkConnectionStatus]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');

        if (code) {
            if (!hasFetchedCode.current) {
                hasFetchedCode.current = true;
                const pendingProvider = localStorage.getItem('pendingWearableProvider') || 'fitbit';
                handleOAuthCallback(code, pendingProvider);
            }
        } else {
            checkConnectionStatus();
        }
    }, [handleOAuthCallback, checkConnectionStatus]);

    const handleConnect = (providerId, authUrl) => {
        setErrorMessage(null);
        if (isPlaceholderAuthUrl(authUrl)) return; // not live yet — button shows "Coming Soon"
        if (!authUrl) {
            alert("Apple Health integration is available on the mobile app only.");
            return;
        }
        localStorage.setItem('pendingWearableProvider', providerId);
        window.location.href = authUrl;
    };

    const handleDisconnect = async (providerId) => {
        if (!window.confirm(`Are you sure you want to disconnect ${providerId}? This will stop data syncing.`)) return;
        setIsDisconnecting(true);
        setErrorMessage(null);
        try {
            const disconnectFn = httpsCallable(functions, 'disconnectWearable');
            await disconnectFn({ provider: providerId });
            setWearableData(prev => {
                const newData = { ...prev };
                delete newData[providerId];
                return newData;
            });
        } catch (error) {
            console.error("Disconnect Error:", error);
            setErrorMessage("Failed to disconnect: " + error.message);
        } finally {
            setIsDisconnecting(false);
        }
    };
    const getProviderConfig = (id) => PROVIDERS.find(p => p.id === id);
    // =========================================================================

    // --- SCORING LOGIC ---
    const scores = useMemo(() => calculateScores(currentUser), [currentUser]);
    const { perfScore, eriScore, hasData, labels, predictions, wellbeingScore, wellbeingTier, quadrant, divergence, safetyFlag } = scores;
    const memberTier = resolveAgeTier(currentUser);
    const isMinor = memberTier === 'kid' || memberTier === 'teen';
    const userName = currentUser?.name?.split(' ')[0] || "Guest";

    // --- PULSE CHECK LOGIC ---
    const isPulseDue = useMemo(() => isPulseDueFor(currentUser, hasData), [currentUser, hasData]);

    // --- SINGLE GATE: content renders ONLY with a baseline AND a fresh weekly pulse ---
    // Preview mode bypasses the gate: a super-admin previewing an org/persona has
    // no persisted assessment, so the gate could never clear — they'd be stuck on
    // the lock screen. Show them the (empty-state) dashboard instead.
    const isLocked = !isPreviewMode && (!hasData || isPulseDue);

    // Weekly action plans now live in src/data/actionPlans.js and are looked up
    // per node inside GuidedPath (shared with the assessment completion screen).

    const PREDICTIVE_METRICS = [
        { id: 'readiness', label: "Daily Readiness", icon: Zap, logic: "Overall system capacity & preparedness.", detail: "How prepared you are to take on the day, based on your recent check-ins. Higher means you have the capacity to push; lower is a cue to ease off and recover." },
        { id: 'burnout', label: "Adaptation Reserve", icon: ShieldAlert, logic: "Your body's current ability to absorb stress.", detail: "How much headroom you have to absorb stress before it wears you down. A full reserve means you are coping well; a low one is a sign to protect your recovery." },
        { id: 'injury', label: "Durability Status", icon: Target, logic: "Tissue resilience & recovery trends.", detail: "How resilient and well-recovered you are trending. Strong means recovery is keeping up; lower suggests you are carrying more strain than you are shedding." },
        { id: 'fatigue', label: "System Charge", icon: Battery, logic: "Current energy battery level.", detail: "Your overall energy level, like a battery. Fully Charged means you are well-rested; Recharging means your body is asking for more rest.", manualValue: labels.sleep === "Poor" ? "Recharging" : (labels.sleep === "Optimal" ? "Fully Charged" : "Stable") }
    ];

    const getValueColor = (id, value) => {
        const valStr = String(value).toLowerCase();
        if (['readiness', 'cognitive'].includes(id)) {
            if (valStr.includes('peak') || valStr.includes('optimal') || valStr.includes('ready') || valStr.includes('good') || valStr.includes('high')) return 'text-green-600 bg-green-50 border-green-100';
            if (valStr.includes('moderate')) return 'text-orange-500 bg-orange-50 border-orange-100';
            return 'text-red-600 bg-red-50 border-red-100'; 
        }
        if (valStr.includes('optimal') || valStr.includes('high') || valStr.includes('capacity') ||
            valStr.includes('charged') || valStr.includes('stable')) return 'text-green-600 bg-green-50 border-green-100';
        if (valStr.includes('moderate') || valStr.includes('elevated') || valStr.includes('adapting')) return 'text-orange-500 bg-orange-50 border-orange-100';
        return 'text-red-600 bg-red-50 border-red-100';
    };

    const trendConfig = {
        'perfScore': { label: 'Overall Performance', color: '#0679af' },
        'sleep_perf': { label: 'Sleep Quality', color: '#8b5cf6' },
        'focus': { label: 'Mental Focus', color: '#10b981' },
        'resilience': { label: 'Resilience', color: '#f59e0b' },
        'motivation': { label: 'Motivation', color: '#ec4899' },
        'confidence': { label: 'Confidence', color: '#eab308' },
        'discipline': { label: 'Self Discipline', color: '#14b8a6' },
        'awareness': { label: 'Self Awareness', color: '#6366f1' },
        'adaptability': { label: 'Adaptability', color: '#f97316' },
        'nutrition': { label: 'Nutrition', color: '#84cc16' },
        'injury_mgmt': { label: 'Pain & Injury', color: '#ef4444' }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-20">
            
            {/* WEARABLE CONNECT MODAL */}
            {showConnectModal && (
                <div className="fixed inset-0 z-[250] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setShowConnectModal(false)}>
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                                <LinkIcon size={20} className="text-brand-primary"/> Link Device
                            </h3>
                            <button onClick={() => setShowConnectModal(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                                <X size={20} className="text-slate-400"/>
                            </button>
                        </div>
                        <div className="p-6 grid gap-4">
                            {PROVIDERS.map(p => {
                                const isComingSoon = isPlaceholderAuthUrl(p.authUrl);
                                const isLive = !!p.authUrl && !isComingSoon;
                                return (
                                <button
                                    key={p.id}
                                    onClick={() => handleConnect(p.id, p.authUrl)}
                                    disabled={isConnecting || isComingSoon}
                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all hover:scale-[1.02] active:scale-95 ${p.bg} ${isLive ? 'hover:shadow-md cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg bg-white/50 ${p.color}`}>{p.icon}</div>
                                        <span className={`font-bold ${p.color}`}>{p.name}</span>
                                    </div>
                                    {isComingSoon && <span className="text-[10px] font-bold uppercase bg-white/50 px-2 py-1 rounded text-slate-500">Coming Soon</span>}
                                    {!p.authUrl && <span className="text-[10px] font-bold uppercase bg-white/50 px-2 py-1 rounded text-slate-500">Mobile Only</span>}
                                    {isLive && isConnecting && <Loader2 size={16} className="animate-spin text-slate-400"/>}
                                </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* PERSONAL CONNECTION OUTLET — opened from the hero or the safety card */}
            {showSupportModal && (
                <SupportContactModal
                    contact={supportContact}
                    eap={eapProgram}
                    focus={supportFocus}
                    // The hero's rose "I need help now" button is the urgent route, so
                    // the crisis block below the EAP would just repeat it. Without an
                    // EAP there is no rose button and the floor has to stay.
                    showCrisisFloor={!eapProgram}
                    onClose={() => { setShowSupportModal(false); setSupportFocus(null); }}
                    memberType={currentUser?.memberType}
                    ageTier={resolveAgeTier(currentUser)}
                />
            )}

            {/* HERO SECTION - ALWAYS VISIBLE */}
            <div className={`${heroClass(orgData)} rounded-3xl p-8 text-white relative overflow-hidden ${isHubTheme(orgData) ? "shadow-sm" : "shadow-2xl"}`} style={heroStyle(orgData)}>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2 text-blue-200 text-xs font-bold uppercase tracking-widest">
                        <Activity size={14} /> Personal Hub
                        {currentUser?.pulseStreak > 0 && (
                            <span className="ml-3 inline-flex items-center gap-1.5 text-orange-400 bg-orange-400/10 px-2.5 py-0.5 rounded-full border border-orange-400/20">
                                🔥 {currentUser.pulseStreak} Week Streak
                                <button type="button" onClick={() => setShowStreakInfo(true)} className="flex items-center text-orange-300/80 hover:text-orange-200 transition-colors" aria-label="What is a streak?">
                                    <Info size={12} />
                                </button>
                            </span>
                        )}

                        {showStreakInfo && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm normal-case tracking-normal" onClick={() => setShowStreakInfo(false)}>
                                <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-sm w-full p-5 animate-in fade-in zoom-in-95 duration-150" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h4 className="font-black text-slate-900 text-base">🔥 Your weekly streak</h4>
                                        <button type="button" onClick={() => setShowStreakInfo(false)} className="text-slate-300 hover:text-slate-500"><X size={18} /></button>
                                    </div>
                                    <p className="text-sm text-slate-500 leading-relaxed font-medium">
                                        Your streak counts how many weeks in a row you have completed your quick pulse check-in. Do your pulse each week to keep it growing &mdash; miss a week and it resets to zero. It is a simple nudge to keep checking in regularly, which is also what keeps your forecast accurate.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Welcome, {userName}.</h1>
                    <p className="text-slate-200 max-w-xl">
                        {!hasData 
                            ? "Complete your baseline assessment to unlock your personal performance metrics."
                            : isPulseDue
                                ? "Your data needs a refresh. Take your quick pulse to unlock this week's AI forecast."
                                : "Your performance stats are mapped. You are ready to execute."
                        }
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={() => onNavigate(isPulseDue && hasData ? 'pulse' : 'assessment')}
                            className="px-6 py-3 bg-white text-brand-primary hover:bg-slate-50 rounded-full font-black transition-all shadow-lg shadow-brand-primary/50 flex items-center gap-2 transform hover:scale-105 active:scale-95"
                        >
                            <Sparkles size={18} className="text-yellow-400 animate-pulse" />
                            {hasData ? (isPulseDue ? "Take Weekly Pulse" : "Update Assessment") : "Start Baseline"}
                        </button>

                        {/* Two outlets, two jobs. With an EAP configured, the chip below
                            carries the everyday "talk to someone" CTA and this button is
                            the urgent one — it opens the same modal but lands on the
                            crisis lines rather than scrolling past the program first.
                            With no EAP there is no chip, so it stays the general outlet. */}
                        <button
                            type="button"
                            onClick={() => {
                                setSupportFocus(eapProgram ? 'crisis' : null);
                                setShowSupportModal(true);
                            }}
                            className={`px-6 py-3 rounded-full font-bold transition-colors flex items-center gap-2 border ${
                                eapProgram
                                    ? 'bg-rose-500/25 hover:bg-rose-500/40 text-white border-rose-200/50'
                                    : 'bg-white/10 hover:bg-white/20 text-white border-white/30'
                            }`}
                        >
                            {eapProgram ? <LifeBuoy size={18} /> : <HeartHandshake size={18} />}
                            {eapProgram ? 'I need help now' : 'Talk to Someone'}
                        </button>
                    </div>

                    {/* The provider's mark, before anything is clicked — the EAP
                        is recognised out here, not discovered inside a modal. */}
                    {eapProgram && (
                        <EapEntryChip
                            eap={eapProgram}
                            onOpen={() => { setSupportFocus(null); setShowSupportModal(true); }}
                            className="mt-4"
                        />
                    )}
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none"></div>
            </div>

            {/* ===== GATE — frosted overlay floats over the blurred dashboard until the baseline is done and this week's pulse is fresh ===== */}
            <div className="relative mt-2">
                {isLocked && (
                    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/40 backdrop-blur-[6px] rounded-3xl border border-white/40 shadow-2xl p-8 text-center">
                        <div className="w-16 h-16 bg-brand-light text-brand-primary rounded-full flex items-center justify-center mb-4 shadow-inner border border-blue-100">
                            <Lock size={32} />
                        </div>
                        {!hasData ? (
                            <>
                                <h2 className="text-2xl font-black text-slate-900 mb-2">Unlock your dashboard</h2>
                                <p className="text-slate-600 max-w-md mb-6 font-medium">
                                    Complete your baseline assessment to reveal your Performance &amp; Wellbeing map and your guided path.
                                </p>
                                <button
                                    onClick={() => onNavigate('assessment')}
                                    className="px-8 py-4 bg-brand-primary text-white rounded-full font-black hover:bg-brand-dark transition-all shadow-xl hover:scale-105 active:scale-95 flex items-center gap-2 text-lg"
                                >
                                    <Sparkles size={20} className="text-yellow-300" />
                                    Start Baseline
                                </button>
                            </>
                        ) : (
                            <>
                                {currentUser?.pulseStreak > 0 ? (
                                    <h2 className="text-3xl font-black text-slate-900 mb-2">Keep your 🔥 {currentUser.pulseStreak}-Week Streak alive!</h2>
                                ) : (
                                    <h2 className="text-2xl font-black text-slate-900 mb-2">{isMinor ? 'Your weekly check-in' : 'Unlock Your AI Forecast'}</h2>
                                )}
                                <p className="text-slate-600 max-w-md mb-6 font-medium">
                                    {isMinor
                                        ? 'Three quick questions, about 30 seconds, and your week is unlocked.'
                                        : 'Complete your 10-second pulse to reveal your updated Adaptation Reserve and Durability Status.'}
                                </p>
                                <button
                                    onClick={() => onNavigate('pulse')}
                                    className="px-8 py-4 bg-brand-primary text-white rounded-full font-black hover:bg-brand-dark transition-all shadow-xl hover:scale-105 active:scale-95 flex items-center gap-2 text-lg"
                                >
                                    <Zap size={24} className="fill-yellow-400 text-yellow-400" />
                                    Take 10-Second Pulse
                                </button>
                            </>
                        )}

                        {/* DUTY OF CARE — one calm support link, only when flagged */}
                        {safetyFlag && (
                            <button
                                type="button"
                                onClick={() => onNavigate('mental-health')}
                                className="mt-5 text-xs font-medium text-slate-400 hover:text-rose-500 underline-offset-2 hover:underline transition-colors"
                            >
                                Need support? Resources are here &rarr;
                            </button>
                        )}
                    </div>
                )}

                {/* BLURRABLE CONTENT WRAPPER — the real dashboard always renders; it just sits blurred behind the gate while locked.
                    While locked we cap the height to a short teaser (max-h + overflow-hidden) so the frosted "unlock" message sits
                    near the top instead of being centered over the full-length dashboard, which forced a long scroll to reach it.
                    Unlocked, the cap is removed and everything expands normally. */}
                <div className={`space-y-6 ${isLocked ? 'filter blur-[8px] opacity-30 pointer-events-none select-none max-h-[420px] overflow-hidden transition-all duration-700' : ''}`}>
                    {/* ===== SECTION: WHERE YOU STAND ===== */}
                    <SectionEyebrow label="Where you stand" sub={`ERI ${eriScore} · ${scores.wellbeingExcluded ? 'performance-led (no wellbeing screen yet)' : 'performance + wellbeing' + (scores.cognitiveExcluded ? '' : ' + cognitive')}`} />

                    {/* PERFORMANCE x WELLBEING MAP */}
                    <PerformanceWellbeingMap
                        perfScore={perfScore}
                        wellbeingScore={wellbeingScore}
                        wellbeingTier={wellbeingTier}
                        quadrant={isMinor ? youthQuadrant(quadrant) : quadrant}
                        tierLabel={isMinor ? youthTier(wellbeingTier) : null}
                        zoneLabels={isMinor ? YOUTH_ZONE_LABELS : null}
                        divergence={divergence}
                        safetyFlag={safetyFlag}
                        hasData={hasData}
                        onNavigate={onNavigate}
                        onGetHelp={() => setShowSupportModal(true)}
                    />

                    {/* ===== SECTION: WHAT TO DO NEXT ===== */}
                    <SectionEyebrow label="What to do next" sub="your guided path — pick anything, we'll recommend the order" />

                    {/* GUIDED PATH — the weekly action plan now lives inside its recommended node */}
                    <GuidedPath
                        scores={scores}
                        currentUser={currentUser}
                        isPulseDue={isPulseDue}
                        onNavigate={onNavigate}
                    />

                    {/* COACH'S MONTHLY FOCUS (SQUAD PLAYBOOK) */}
                    {activePlaybook && (
                        <div className="bg-white p-6 md:p-8 rounded-3xl border-2 border-brand-primary/20 shadow-lg relative overflow-hidden animate-in slide-in-from-bottom-4">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 text-brand-primary mb-3">
                                    <Target size={20}/>
                                    <span className="font-bold text-xs uppercase tracking-widest">{adaptText(playbookAuthor)}'s Team Focus</span>
                                </div>
                                <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-2">{adaptText(activePlaybook.title)}</h3>
                                <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-6">{adaptText(activePlaybook.description)}</p>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {activePlaybook.actions.map((action, idx) => (
                                        <div key={idx} className="flex flex-col gap-2 p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:border-brand-primary/30 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-6 h-6 rounded-full bg-blue-100 text-brand-primary flex items-center justify-center font-black shrink-0 text-xs">{idx + 1}</div>
                                                <h4 className="font-bold text-slate-800 text-sm leading-tight">{adaptText(action.title)}</h4>
                                            </div>
                                            <p className="text-xs text-slate-500 leading-relaxed pl-9">{adaptText(action.text)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ===== SECTION: HOW YOU'RE TRENDING ===== */}
                    <SectionEyebrow label="How you're trending" sub="the detail, once you know the headline" />

                    <div className="flex flex-col gap-6">
                        {/* HISTORICAL TRENDLINE CHART (full width) */}
                        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col relative overflow-hidden">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                                <div className="flex items-center gap-2">
                                    <LineChartIcon size={20} className="text-brand-primary" />
                                    <h3 className="text-lg font-black text-slate-800">Your Personal Trendline</h3>
                                </div>
                                <select 
                                    className="bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl px-3 py-2 outline-none focus:border-brand-primary"
                                    value={selectedTrend}
                                    onChange={(e) => setSelectedTrend(e.target.value)}
                                >
                                    {Object.entries(trendConfig).map(([key, config]) => (
                                        <option key={key} value={key}>{config.label}</option>
                                    ))}
                                </select>
                            </div>

                            {isHistoryLoading ? (
                                <div className="flex-1 flex items-center justify-center min-h-[250px]">
                                    <Loader2 className="animate-spin text-slate-300" size={32}/>
                                </div>
                            ) : historicalData.length < 2 ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 min-h-[250px] bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                    <LineChartIcon size={32} className="mb-2 opacity-30" />
                                    <p className="text-sm font-medium">Not enough data to map trends.</p>
                                    <p className="text-xs">Complete at least two assessments to see your chart.</p>
                                </div>
                            ) : (
                                <div className="flex-1 min-h-[250px] -ml-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={historicalData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorOverall" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={trendConfig['perfScore'].color} stopOpacity={0.3}/>
                                                    <stop offset="95%" stopColor={trendConfig['perfScore'].color} stopOpacity={0}/>
                                                </linearGradient>
                                                {selectedTrend !== 'perfScore' && (
                                                    <linearGradient id="colorSelected" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={trendConfig[selectedTrend].color} stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor={trendConfig[selectedTrend].color} stopOpacity={0}/>
                                                    </linearGradient>
                                                )}
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="dateStr" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8'}} dy={10} />
                                            <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8'}} />
                                            <Tooltip 
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                                labelStyle={{ fontWeight: 'bold', color: '#475569', marginBottom: '4px' }}
                                            />
                                            
                                            <Area 
                                                type="monotone" 
                                                dataKey="perfScore" 
                                                name={trendConfig['perfScore'].label}
                                                stroke={trendConfig['perfScore'].color} 
                                                strokeWidth={selectedTrend === 'perfScore' ? 4 : 2}
                                                strokeDasharray={selectedTrend === 'perfScore' ? "0" : "4 4"}
                                                fillOpacity={1} 
                                                fill="url(#colorOverall)" 
                                                activeDot={{ r: selectedTrend === 'perfScore' ? 6 : 4, strokeWidth: 0 }}
                                            />

                                            {selectedTrend !== 'perfScore' && (
                                                <Area 
                                                    type="monotone" 
                                                    dataKey={selectedTrend} 
                                                    name={trendConfig[selectedTrend].label}
                                                    stroke={trendConfig[selectedTrend].color} 
                                                    strokeWidth={4}
                                                    fillOpacity={1} 
                                                    fill="url(#colorSelected)" 
                                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                                />
                                            )}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>

                        {/* PREDICTIVE INSIGHTS — AI FORECAST (full width, stacked below the trend).
                            Not shown to under-18s: injury and burnout forecasts are not words for a
                            12-year-old's home page. */}
                        {!isMinor && (
                        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2 mb-4">
                            <TrendingUp size={20} className="text-brand-primary" /> 
                            My AI Forecast <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Live Model</span>
                            <button type="button" onClick={() => setShowForecastInfo(v => !v)} className="ml-auto flex items-center gap-1 text-xs font-bold text-brand-primary hover:text-brand-dark transition-colors shrink-0">
                                <Info size={14} /> What do these mean?
                            </button>
                        </h3>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {PREDICTIVE_METRICS.map((item, idx) => {
                                const displayValue = predictions?.[item.id] || item.manualValue || "--";
                                const valueColor = getValueColor(item.id, displayValue);
                                const isDisabled = !!item.disabled;

                                return (
                                    <div key={idx} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-center text-center relative overflow-hidden group h-full justify-between">
                                        {!isDisabled && !hasData && (
                                            <div className="absolute inset-0 bg-slate-50/50 backdrop-blur-[1px] flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span className="text-[10px] font-bold text-slate-500 bg-white px-2 py-1 rounded-full shadow-sm border border-slate-200">Learning...</span>
                                            </div>
                                        )}
                                        
                                        <div className={`w-full flex flex-col items-center h-full justify-between`}>
                                            <div className="mb-3 p-2 bg-slate-50 text-slate-400 rounded-xl group-hover:text-brand-primary group-hover:bg-blue-50 transition-colors shrink-0">
                                                {React.createElement(item.icon, { size: 24 })}
                                            </div>
                                            <div className="h-8 flex items-center justify-center w-full mb-1">
                                                <h4 className="font-bold text-sm text-slate-700 leading-tight">{item.label}</h4>
                                            </div>
                                            <div className="h-6 flex items-center justify-center w-full mb-3">
                                                <span className={`text-[10px] font-black uppercase tracking-tight whitespace-nowrap px-3 py-1.5 rounded-lg border ${valueColor} w-full max-w-[120px] text-center`}>
                                                    {hasData ? displayValue : "--"}
                                                </span>
                                            </div>
                                            <div className="border-t border-slate-50 pt-3 w-full">
                                                <p className="text-[10px] text-slate-400 font-medium leading-tight px-1 line-clamp-2 min-h-[2.2em] flex items-center justify-center">
                                                    {item.logic}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {showForecastInfo && (
                            <div className="mt-4 bg-slate-50 rounded-2xl border border-slate-100 p-5 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <p className="text-xs text-slate-500 leading-relaxed font-medium">
                                        Your AI Forecast is a forward-looking read on how you are trending, built from your own check-in data. These are modeled signals to help you plan your week &mdash; not medical or clinical predictions.
                                    </p>
                                    <button type="button" onClick={() => setShowForecastInfo(false)} className="shrink-0 text-slate-300 hover:text-slate-500"><X size={14} /></button>
                                </div>
                                <div className="space-y-3 border-t border-slate-50 pt-3">
                                    {PREDICTIVE_METRICS.map((item) => (
                                        <div key={item.id} className="flex items-start gap-3">
                                            <div className="shrink-0 mt-0.5 p-1.5 bg-slate-50 text-brand-primary rounded-lg">
                                                {React.createElement(item.icon, { size: 14 })}
                                            </div>
                                            <div>
                                                <p className="font-bold text-xs text-slate-800">{item.label}</p>
                                                <p className="text-[11px] text-slate-500 leading-relaxed">{item.detail || item.logic}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        </div>
                        )}
                    </div>

                    {/* ===== DEVICES — quiet, last row. Not offered to under-18s. ===== */}
                    {!isMinor && (
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-4 px-2">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <Watch size={20} className="text-brand-primary" /> Connected Devices
                            </h3>
                            {Object.keys(wearableData).length > 0 && (
                                <button 
                                    onClick={() => setShowConnectModal(true)}
                                    className="text-xs font-bold text-brand-primary hover:text-brand-dark flex items-center gap-1 bg-blue-50 px-3 py-1.5 rounded-full transition-colors"
                                >
                                    <Plus size={14} /> Add Another
                                </button>
                            )}
                        </div>
                        
                        {errorMessage && (
                            <div className="mb-4 p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl flex items-center gap-2">
                                <AlertCircle size={16} /> {errorMessage}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* RENDER CONNECTED DEVICES */}
                            {Object.entries(wearableData).map(([providerId, data]) => {
                                const config = getProviderConfig(providerId);
                                if (!config) return null;
                                const isSyncingThis = syncStatus === 'syncing';

                                return (
                                    <div key={providerId} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
                                        {/* HEADER BAR */}
                                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-xl ${config.bg} ${config.color}`}>
                                                    {config.icon}
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-800 text-sm">{config.name}</h4>
                                                    <p className="text-[10px] text-slate-400 font-medium">
                                                        Synced: {data.syncedAt ? new Date(data.syncedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 'Just now'}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => handleRefreshData(providerId)}
                                                    disabled={isSyncingThis}
                                                    className="p-2 bg-white border border-slate-200 text-brand-primary rounded-lg hover:bg-blue-50 transition-colors"
                                                    title="Sync Data"
                                                >
                                                    <RefreshCw size={16} className={isSyncingThis ? 'animate-spin' : ''} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDisconnect(providerId)}
                                                    disabled={isDisconnecting || isSyncingThis}
                                                    className="p-2 bg-white border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-100 transition-colors rounded-lg"
                                                    title="Disconnect"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* DATA AREA */}
                                        <div className="p-4 flex-1 flex flex-col gap-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                {/* Score/Recovery */}
                                                {(data.recoveryScore !== undefined || data.readinessScore !== undefined) && (
                                                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 flex flex-col items-center justify-center text-center">
                                                        <span className="text-xl font-black text-slate-800">
                                                            {data.recoveryScore !== undefined ? `${data.recoveryScore}%` : data.readinessScore}
                                                        </span>
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase">{data.recoveryScore !== undefined ? 'Recovery' : 'Readiness'}</p>
                                                    </div>
                                                )}
                                                
                                                {/* Sleep */}
                                                {data.sleepTotal !== undefined && (
                                                    <div className="bg-indigo-50 p-3 rounded-xl border border-indigo-100 flex flex-col items-center justify-center text-center">
                                                        <span className="text-xl font-black text-indigo-700">
                                                            {Math.floor(data.sleepTotal/60)}<span className="text-sm">h</span> {data.sleepTotal%60}<span className="text-sm">m</span>
                                                        </span>
                                                        <p className="text-[10px] font-bold text-indigo-400 uppercase">Sleep</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                                                <div className="text-center">
                                                    <p className="text-sm font-bold text-slate-700">{data.restingHeartRate || '--'}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase">RHR</p>
                                                </div>
                                                {data.hrv && (
                                                    <div className="text-center">
                                                        <p className="text-sm font-bold text-slate-700">{data.hrv}</p>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase">HRV</p>
                                                    </div>
                                                )}
                                                <div className="text-center">
                                                    <p className="text-sm font-bold text-slate-700">{data.strain || data.activeMinutes || '--'}</p>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase">{data.strain ? 'Strain' : 'Active Mins'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* 'CONNECT' BUTTON (Visible if no devices or as an empty card) */}
                            {Object.keys(wearableData).length === 0 ? (
                                <button 
                                    onClick={() => setShowConnectModal(true)}
                                    className="col-span-1 lg:col-span-3 py-8 border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center text-slate-400 hover:border-brand-primary hover:text-brand-primary hover:bg-blue-50 transition-all gap-3 group"
                                >
                                    <div className="p-4 bg-slate-100 rounded-full group-hover:bg-white transition-colors shadow-sm">
                                        <Plus size={32} />
                                    </div>
                                    <span className="font-bold text-base">Connect Wearable Device</span>
                                    <span className="text-xs opacity-70">Fitbit, Oura, Whoop</span>
                                </button>
                            ) : null}
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardView;